<script setup lang="ts">
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select'
import SelectValue from '@/components/ui/select/SelectValue.vue'
import { useCartTotalsStore } from '@/composables/useCartTotalsStore'
import { cardNumber, expirationDate, securityCode } from '@/helpers/credit-card'
import { formatPrice } from '@/helpers/current-price'
import { formatCpfCnpj } from '@/helpers/document'
import { formatText } from '@/helpers/format-text'
import { FormSchema } from '@/pages/carts/show.vue'
import { useDebounceFn } from '@vueuse/core'
import { AcceptableValue } from 'reka-ui'
import { onBeforeMount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { toast } from 'vue-sonner'
import PaymentCard from '../payment-card/PaymentCard.vue'

const { t } = useI18n()
const props = defineProps<{
    form: FormSchema
}>()

interface Installments {
    installment_plans: {
        installments: number
        installment_value: number
        interest_free: boolean
        amount: {
            value: number
            currency: 'BRL'
            fees: number | null
        }
    }[]
    final_total: number
}

const installments = ref<Installments | null>(null)

let lastBin: string | null = null

const getInstallments = useDebounceFn(async () => {
    const creditCard = props.form.card_number.replace(/\s+/g, '').replace(/[^0-9]/gi, '')

    if (creditCard.length > 16) return

    const bin = creditCard.substring(0, 6)

    if (bin.length < 6 || bin === lastBin) return

    lastBin = bin

    try {
        const res = await fetch(`/checkout/installment_plans?bin=${bin}`, {})
        const data = (await res.json()) as Installments
        installments.value = data
    } catch {
        toast.error(t('Error fetching installments'))
    }
}, 300)

const { installmentTotals } = useCartTotalsStore()

function handleInstallments(value: AcceptableValue) {
    const installment = installments.value?.installment_plans.find((ins) => ins.installments === Number(value))

    if (installment) installmentTotals.value = installment?.amount.value / 100
}

onBeforeMount(async () => {
    if (props.form.card_number && props.form.card_number.length > 6) {
        props.form.card_number = cardNumber(props.form.card_number)
        await getInstallments()

        if (props.form.installments) {
            handleInstallments(props.form.installments)
        }
    }
})
</script>

<template>
    <PaymentCard :title="$t('Card data')" class="mt-4">
        <section class="grid gap-8 gap-y-3 md:grid-cols-3 md:gap-y-6">
            <div class="col-span-full">
                <Label required for="payment_name">{{ $t('Name printed on card') }}</Label>
                <Input
                    type="text"
                    name="payment_name"
                    id="payment_name"
                    v-model="form.card_holder"
                    :placeholder="$t('Tap your name')"
                    @input="(e: Event) => (form.card_holder = formatText((e.target as HTMLInputElement).value))"
                />
                <p class="text-destructive mt-px text-xs" v-if="form.errors.card_holder">{{ form.errors.card_holder }}</p>
            </div>
            <div class="col-span-full">
                <Label required for="payment_name">{{ $t('CPF or CNPJ') }}</Label>
                <Input
                    v-if="$i18n.locale === 'pt-BR'"
                    type="text"
                    name="payment_name"
                    id="payment_name"
                    v-model="form.card_ssn_ein"
                    :placeholder="$t('CPF or CNPJ')"
                    @input="(e: Event) => (form.card_ssn_ein = formatCpfCnpj((e.target as HTMLInputElement).value))"
                    maxlength="18"
                />
                <Input v-else type="text" name="payment_name" id="payment_name" v-model="form.card_ssn_ein" :placeholder="$t('Tap your name')" />
                <p class="text-destructive mt-px text-xs" v-if="form.errors.card_ssn_ein">{{ $t(form.errors.card_ssn_ein) }}</p>
            </div>

            <div>
                <Label required for="card_number">{{ $t('Card number') }}</Label>
                <Input
                    type="text"
                    name="card_number"
                    id="card_number"
                    v-model="form.card_number"
                    placeholder="0000 0000 0000 0000"
                    @input="
                        (e: Event) => {
                            props.form.card_number = cardNumber((e.target as HTMLInputElement).value)
                            getInstallments()
                        }
                    "
                />
                <p class="text-destructive mt-px text-xs" v-if="form.errors.card_number">{{ $t(form.errors.card_number) }}</p>
            </div>
            <div>
                <Label required for="expiratio_date">{{ $t('Expiration date') }}</Label>
                <Input
                    type="text"
                    name="expiratio_date"
                    id="expiratio_date"
                    v-model="form.card_exp"
                    :placeholder="$t('MM/YY')"
                    @input="
                        (e: Event) => {
                            form.card_exp = expirationDate((e.target as HTMLInputElement).value)
                        }
                    "
                />
                <p class="text-destructive mt-px text-xs" v-if="form.errors.card_exp">{{ $t(form.errors.card_exp) }}</p>
            </div>
            <div>
                <Label required for="security_code">{{ $t('Security code') }}</Label>
                <Input
                    type="text"
                    name="security_code"
                    id="security_code"
                    v-model="form.card_cvv"
                    placeholder="000"
                    @input="(e: Event) => (form.card_cvv = securityCode((e.target as HTMLInputElement).value))"
                />
                <p class="text-destructive mt-px text-xs" v-if="form.errors.card_cvv">{{ $t(form.errors.card_cvv) }}</p>
            </div>
            <div class="col-span-full">
                <Label required for="number_of_stallments">{{ $t('Number of installments') }}</Label>

                <Select
                    id="number_of_stallments"
                    v-model="form.installments"
                    name="number_of_stallments"
                    class="w-full"
                    default-value="1"
                    @update:model-value="(value) => handleInstallments(value)"
                    :disabled="form.card_number?.replace(/\s+/g, '').replace(/[^0-9]/gi, '').length < 6"
                >
                    <SelectTrigger class="w-full">
                        <SelectValue :placeholder="$t('Select the number of installments')" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem
                            v-for="installment in installments?.installment_plans"
                            :key="installment.installment_value"
                            :value="String(installment.installments)"
                            >{{ installment.installments }}x de {{ formatPrice(installment.amount.value / installment.installments / 100) }}
                            <template v-if="installment.interest_free">{{ $t('Without Interest') }}</template>
                            <template v-else>{{ $t('With Interest') }}</template>
                        </SelectItem>
                    </SelectContent>
                </Select>
                <p class="text-destructive mt-px text-xs" v-if="form.errors.installments">{{ $t(form.errors.installments) }}</p>
            </div>
        </section>
    </PaymentCard>
</template>
